import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { Group, Role } from '@esg/esg-global-types';

import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import {
  createUsers,
  checkExistingEmails,
  type CreateUsersRow
} from '../../graphql/services/user.service';
import { generateUuidV4 } from '../../util/uuid';
import { sendNewUserWelcomeEmailsBulk } from '../../lib/user_management/welcome_email';
import { parseCsv } from './import/parse_csv';
import { parseSpreadsheet, isExcelFile, createExcelTemplateBlob } from './import/parse_spreadsheet';
import { CSV_IMPORT_ROW_SCHEMA, CSV_IMPORT_MAX_ROWS, type CsvImportRow } from './import/schemas';
import GroupCompanyAccessPicker from './shared/GroupCompanyAccessPicker';
import {
  accessGrantCount,
  buildGroupsPayload,
  countSelection,
  describeSelection,
  hasAnyCompany,
  MAX_ACCESS_GRANTS_PER_IMPORT,
  type AccessSelection
} from './shared/access_selection';

/**
 * Step-0 helper copy. Hoisted because JSX collapses whitespace in children but
 * preserves it verbatim inside string-literal attributes, so an inline
 * multi-line prop would ship the newline and its indentation.
 */
const ACCESS_STEP_DESCRIPTION =
  'Pick the access this batch will receive. Every row in the file is added to every company ' +
  'selected below, across as many groups as you need.';

interface ImportUsersFromCsvDrawerProps {
  groups: Group[];
  roles: Role[];
  /** Called after the import completes, with the count of created users. */
  onImported: (count: number) => void;
  onClose: () => void;
}

const STEPS = ['Assign access', 'Upload', 'Preview & validate', 'Confirm & import'];

interface ParsedRow {
  /** Stable identity for React keys across edits/removals. */
  uid: string;
  raw: Record<string, string>;
  parsed: CsvImportRow | null;
  /** Per-field validation error messages, keyed by field name. */
  fieldErrors: Record<string, string>;
}

const FIELD_KEYS: ReadonlyArray<keyof CsvImportRow> = ['email', 'first_name', 'last_name', 'type'];

/** Validates a raw CSV row against the import schema, mapping per-field errors. */
const validateRow = (raw: Record<string, string>, uid: string): ParsedRow => {
  const result = CSV_IMPORT_ROW_SCHEMA.safeParse(raw);
  if (result.success) {
    return { uid, raw, parsed: result.data, fieldErrors: {} };
  }
  const fieldErrors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = String(issue.path[0] ?? '_');
    if (!fieldErrors[key]) fieldErrors[key] = issue.message;
  }
  return { uid, raw, parsed: null, fieldErrors };
};

interface ImportResult {
  created: number;
  skipped_existing: number;
  failed: number;
  /**
   * Distinct per-row error strings from the bulk envelope. Rows fail for
   * reasons the operator cannot otherwise see — an out-of-scope group rejects
   * every row, and each (group, company) pair is its own subscription-quota
   * check — so the reason has to reach the result panel, not just a count.
   */
  failureReasons: string[];
  emailsSent: number;
  emailsFailed: number;
  /** True when the admin turned off welcome emails for this import (not a failure). */
  welcomeEmailsSkipped: boolean;
}

/** Distinct failure reasons, most-common first, capped for display. */
const summariseFailures = (failures: Array<{ error: string }>, limit = 3): string[] => {
  const counts = new Map<string, number>();
  for (const failure of failures) {
    const reason = (failure.error ?? '').trim() || 'Unknown error.';
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([reason, count]) => (count > 1 ? `${reason} (${count} rows)` : reason));
};

/** CSV import wizard: assign access, upload, preview/validate, confirm & import. */
const ImportUsersFromCsvDrawer = ({
  groups,
  roles,
  onImported,
  onClose
}: ImportUsersFromCsvDrawerProps) => {
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<number>(0);
  const [selection, setSelection] = useState<AccessSelection>({});
  const [role, setRole] = useState<string>('viewer');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [skipErrors, setSkipErrors] = useState<boolean>(true);
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState<boolean>(true);
  const [importing, setImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<{
    stage: 'creating' | 'emailing' | null;
    done: number;
    total: number;
  }>({ stage: null, done: 0, total: 0 });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  /**
   * Authoritative set of already-existing emails from the backend, scoped to
   * this CSV's addresses so detection isn't capped by how many users are loaded
   * client-side (case-insensitive, includes soft-deleted users).
   */
  const [existingEmailSet, setExistingEmailSet] = useState<Set<string>>(new Set());

  /** Content-keyed list of the CSV's unique emails; re-checked only when it changes. */
  const csvEmailsKey = useMemo(
    () =>
      [
        ...new Set(
          parsedRows
            .map((row) => row.raw.email?.trim().toLowerCase())
            .filter((email): email is string => !!email)
        )
      ]
        .sort()
        .join('\n'),
    [parsedRows]
  );

  useEffect(() => {
    const emails = csvEmailsKey ? csvEmailsKey.split('\n') : [];
    if (emails.length === 0) {
      setExistingEmailSet(new Set());
      return undefined;
    }
    let cancelled = false;
    checkExistingEmails(emails)
      .then((result) => {
        if (!cancelled) setExistingEmailSet(result);
      })
      .catch(() => {
        // Backend check failed — nothing flagged as a duplicate this round;
        // the create step's own per-row email-exists handling is still a
        // backstop against actually creating a duplicate.
        if (!cancelled) setExistingEmailSet(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [csvEmailsKey]);

  /** Augments parsed rows with in-file and against-existing duplicate detection. */
  const rowsWithDuplicates: ParsedRow[] = useMemo(() => {
    const emailCounts = new Map<string, number>();
    for (const row of parsedRows) {
      const email = row.raw.email?.trim().toLowerCase();
      if (!email) continue;
      emailCounts.set(email, (emailCounts.get(email) ?? 0) + 1);
    }
    return parsedRows.map((row) => {
      const email = row.raw.email?.trim().toLowerCase();
      if (!email) return row;
      const csvDup = (emailCounts.get(email) ?? 0) > 1;
      const existingDup = existingEmailSet.has(email);
      if (!csvDup && !existingDup) return row;
      const dupMessage = csvDup ? 'Duplicate email in this file' : 'Email already exists';
      return {
        uid: row.uid,
        raw: row.raw,
        parsed: null,
        fieldErrors: {
          ...row.fieldErrors,
          email: row.fieldErrors.email ?? dupMessage
        }
      };
    });
  }, [parsedRows, existingEmailSet]);

  const validRows = useMemo(
    () => rowsWithDuplicates.filter((r) => r.parsed !== null),
    [rowsWithDuplicates]
  );
  const invalidRows = useMemo(
    () => rowsWithDuplicates.filter((r) => r.parsed === null),
    [rowsWithDuplicates]
  );

  const processFile = async (file: File) => {
    const name = file.name.toLowerCase();
    const isCsv = name.endsWith('.csv') || file.type === 'text/csv';
    const excel = isExcelFile(file);
    if (!isCsv && !excel) {
      setFileName(file.name);
      setParseError('That file is not a CSV or Excel file. Please choose a .csv or .xlsx file.');
      setParsedRows([]);
      return;
    }
    setFileName(file.name);
    setParseError(null);
    try {
      const { headers, rows } = excel
        ? await parseSpreadsheet(await file.arrayBuffer())
        : parseCsv(await file.text());
      const required = ['email', 'first_name', 'last_name'];
      const missing = required.filter((h) => !headers.includes(h));
      if (missing.length > 0) {
        setParseError(`File is missing required column(s): ${missing.join(', ')}`);
        setParsedRows([]);
        return;
      }
      if (rows.length === 0) {
        setParseError('The file has a header but no data rows.');
        setParsedRows([]);
        return;
      }
      if (rows.length > CSV_IMPORT_MAX_ROWS) {
        setParseError(`Too many rows: ${rows.length}. Max is ${CSV_IMPORT_MAX_ROWS} per import.`);
        setParsedRows([]);
        return;
      }
      setParsedRows(rows.map((row) => validateRow(row, generateUuidV4())));
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to read file');
      setParsedRows([]);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      await processFile(file);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const [isDragging, setIsDragging] = useState<boolean>(false);

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.types.includes('Files')) {
      event.dataTransfer.dropEffect = 'copy';
      if (!isDragging) setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) await processFile(file);
  };

  /** Inline-edit handler: updates a raw cell, re-validates, and re-renders. */
  const updateRowField = (idx: number, field: string, value: string) => {
    setParsedRows((rows) =>
      rows.map((row, i) => (i === idx ? validateRow({ ...row.raw, [field]: value }, row.uid) : row))
    );
  };

  /** Removes a row from the import set. */
  const removeRow = (idx: number) => {
    setParsedRows((rows) => rows.filter((_, i) => i !== idx));
  };

  // Subscription writes this import would perform: one per row, per selected
  // company. Checked before submitting so an oversized batch fails as a
  // message rather than as a request timeout mid-import.
  const grantCount = accessGrantCount(validRows.length, selection);
  const grantsExceeded = grantCount > MAX_ACCESS_GRANTS_PER_IMPORT;

  const canAdvance = (() => {
    if (step === 0) {
      return hasAnyCompany(selection);
    }
    if (step === 1) return !!fileName && parseError === null && parsedRows.length > 0;
    if (step === 2)
      return validRows.length > 0 && (skipErrors || invalidRows.length === 0) && !grantsExceeded;
    return false;
  })();

  const buildRow = (parsed: CsvImportRow): CreateUsersRow => ({
    id: generateUuidV4(),
    email: parsed.email,
    first_name: parsed.first_name,
    last_name: parsed.last_name,
    phone: '',
    super_admin: false,
    type: parsed.type,
    groups: buildGroupsPayload(
      selection,
      role,
      groups.map((groupOption) => groupOption.id)
    )
  });

  const handleImport = async () => {
    // Unreachable through the step machine, which gates step 0 on the same
    // predicate — but a future step reorder must not be able to post 500 rows
    // carrying `groups: {}`.
    if (!hasAnyCompany(selection)) {
      setFeedbackData({
        message: 'Select at least one company before importing.',
        state: true,
        type: 'error'
      });
      return;
    }
    if (grantsExceeded) {
      setFeedbackData({
        message: `Too many access grants for one import (${grantCount.toLocaleString()} of ${MAX_ACCESS_GRANTS_PER_IMPORT.toLocaleString()}). Reduce the selected companies or split the file.`,
        state: true,
        type: 'error'
      });
      return;
    }
    if (!group || !company) {
      setFeedbackData({
        message: 'Group + company context missing — refresh and try again.',
        state: true,
        type: 'error'
      });
      return;
    }
    setImporting(true);
    const rowsToImport = validRows.map((r) => buildRow(r.parsed!));
    setImportProgress({ stage: 'creating', done: 0, total: rowsToImport.length });
    try {
      const envelope = await createUsers(
        { group_id: group.id, company_id: company.id },
        rowsToImport
      );
      let emailsSent = 0;
      let emailsFailed = 0;
      if (sendWelcomeEmails && envelope.successes.length > 0) {
        setImportProgress({
          stage: 'emailing',
          done: 0,
          total: envelope.successes.length
        });
        const nameByEmail = new Map(rowsToImport.map((r) => [r.email, r.first_name]));
        // One request per template bucket — no per-user fan-out.
        const result = await sendNewUserWelcomeEmailsBulk(
          envelope.successes.map((u) => ({
            name: nameByEmail.get(u.email) ?? u.first_name ?? '',
            email: u.email
          })),
          'csv_import'
        );
        emailsSent = result.sent_emails.length;
        // Freshly-created users have no prior invite, so cooldown-skips are not
        // expected here; fold any into failed so nothing is silently lost.
        emailsFailed = result.failed_emails.length + result.skipped_cooldown_emails.length;
        setImportProgress((cur) => ({ ...cur, done: envelope.successes.length }));
      }
      const result: ImportResult = {
        created: envelope.successes.length,
        skipped_existing: envelope.skipped.length,
        failed: envelope.failures.length,
        failureReasons: summariseFailures(envelope.failures),
        emailsSent,
        emailsFailed,
        welcomeEmailsSkipped: !sendWelcomeEmails
      };
      setImportResult(result);
      onImported(result.created);
      setFeedbackData({
        message:
          result.failed === 0
            ? `Imported ${result.created} user${result.created === 1 ? '' : 's'}`
            : `${result.created} imported, ${result.failed} failed`,
        state: true,
        type: result.failed === 0 ? 'success' : 'warning'
      });
    } catch (err) {
      setFeedbackData({
        message: err instanceof Error ? err.message : 'Bulk import failed',
        state: true,
        type: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsvTemplate = () => {
    const csv = 'email,first_name,last_name,type\nalice@example.com,Alice,Doe,internal\n';
    downloadBlob(new Blob([csv], { type: 'text/csv' }), 'syntiro_users_template.csv');
  };

  const downloadExcelTemplate = async () => {
    const blob = await createExcelTemplateBlob();
    downloadBlob(blob, 'syntiro_users_template.xlsx');
  };

  return (
    <Box sx={{ width: 960, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Bulk import users
        </Typography>
        <IconButton onClick={onClose} aria-label="Close drawer">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ p: 3, pb: 0 }}>
        <Stepper activeStep={step}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <GroupCompanyAccessPicker
              groups={groups}
              roles={roles}
              value={selection}
              onChange={setSelection}
              role={role}
              onRoleChange={setRole}
              description={ACCESS_STEP_DESCRIPTION}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={sendWelcomeEmails}
                  onChange={(e) => setSendWelcomeEmails(e.target.checked)}
                />
              }
              label="Send welcome emails"
            />
            {!sendWelcomeEmails && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                Users will be created without an invitation email. Use &quot;Resend invite&quot;
                from the list later when you&apos;re ready to send it.
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={downloadCsvTemplate}>
                Download CSV template
              </Button>
              <Button variant="outlined" onClick={downloadExcelTemplate}>
                Download Excel template
              </Button>
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV or Excel file with columns: <code>email</code>, <code>first_name</code>,{' '}
              <code>last_name</code>. <code>type</code> (internal / external / any) is optional and
              defaults to <code>internal</code> when blank or omitted. Up to {CSV_IMPORT_MAX_ROWS}{' '}
              rows per import.
            </Typography>
            <Box
              component="label"
              htmlFor="csv-import-file-input"
              onClick={(e: React.MouseEvent<HTMLLabelElement>) => {
                if (e.target === fileInputRef.current) return;
                e.preventDefault();
                fileInputRef.current?.click();
              }}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 5,
                px: 3,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: isDragging ? 'action.hover' : 'background.default',
                color: 'text.secondary',
                cursor: 'pointer',
                transition: 'background-color 120ms, border-color 120ms',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                },
                '&:focus-within': {
                  outline: 'none',
                  borderColor: 'primary.main',
                  boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.light}`
                }
              }}
            >
              <input
                ref={fileInputRef}
                id="csv-import-file-input"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileSelect}
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0,0,0,0)',
                  whiteSpace: 'nowrap',
                  border: 0
                }}
              />
              <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="subtitle1" color="text.primary">
                {isDragging
                  ? 'Drop the file to upload'
                  : 'Drag a CSV or Excel file here or click to browse'}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                .csv or .xlsx · max {CSV_IMPORT_MAX_ROWS} rows
              </Typography>
            </Box>
            {fileName && (
              <Typography variant="body2">
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }}
                />
                {fileName} — {parsedRows.length} row
                {parsedRows.length === 1 ? '' : 's'} parsed
              </Typography>
            )}
            {parseError && (
              <Typography variant="body2" color="error">
                {parseError}
              </Typography>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip color="success" label={`${validRows.length} ready`} />
              {invalidRows.length > 0 && (
                <Chip color="error" label={`${invalidRows.length} need attention`} />
              )}
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <label
                  htmlFor="skip_errors"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer'
                  }}
                >
                  <input
                    id="skip_errors"
                    type="checkbox"
                    checked={skipErrors}
                    onChange={(e) => setSkipErrors(e.target.checked)}
                  />
                  Skip rows with errors
                </label>
              </Box>
            </Box>
            {grantsExceeded && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {validRows.length} rows × {countSelection(selection).companies} companies ={' '}
                {grantCount.toLocaleString()} access grants, over the{' '}
                {MAX_ACCESS_GRANTS_PER_IMPORT.toLocaleString()} limit for one import. Reduce the
                companies on step 1, or split the file into smaller batches.
              </Typography>
            )}
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: 56 }} />
                    <TableCell>Email</TableCell>
                    <TableCell>First name</TableCell>
                    <TableCell>Last name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Issue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsWithDuplicates.map((row, idx) => (
                    <TableRow
                      key={row.uid}
                      sx={{
                        bgcolor: row.parsed === null ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                        '& .MuiTableCell-root': { verticalAlign: 'top', py: 1 }
                      }}
                    >
                      <TableCell align="center" sx={{ width: 56 }}>
                        <Tooltip title="Remove this row from the import">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeRow(idx)}
                            aria-label={`Remove row ${idx + 1}`}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <TextField
                          value={row.raw.email ?? ''}
                          onChange={(e) => updateRowField(idx, 'email', e.target.value)}
                          error={!!row.fieldErrors.email}
                          helperText={row.fieldErrors.email}
                          size="small"
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          value={row.raw.first_name ?? ''}
                          onChange={(e) => updateRowField(idx, 'first_name', e.target.value)}
                          error={!!row.fieldErrors.first_name}
                          helperText={row.fieldErrors.first_name}
                          size="small"
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          value={row.raw.last_name ?? ''}
                          onChange={(e) => updateRowField(idx, 'last_name', e.target.value)}
                          error={!!row.fieldErrors.last_name}
                          helperText={row.fieldErrors.last_name}
                          size="small"
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 110 }}>
                        <TextField
                          select
                          value={
                            ['internal', 'external', 'any'].includes(row.raw.type ?? '')
                              ? row.raw.type
                              : (row.parsed?.type ?? '')
                          }
                          onChange={(e) => updateRowField(idx, 'type', e.target.value)}
                          error={!!row.fieldErrors.type}
                          helperText={row.fieldErrors.type}
                          size="small"
                          variant="standard"
                          fullWidth
                        >
                          <MenuItem value="" disabled>
                            {row.raw.type ? `(${row.raw.type})` : 'Select'}
                          </MenuItem>
                          <MenuItem value="internal">internal</MenuItem>
                          <MenuItem value="external">external</MenuItem>
                          <MenuItem value="any">any</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {row.parsed === null ? (
                          <Chip
                            size="small"
                            color="error"
                            label={
                              FIELD_KEYS.map((k) => row.fieldErrors[k]).find((m) => !!m) ??
                              'Invalid'
                            }
                            variant="outlined"
                          />
                        ) : (
                          <Chip size="small" color="success" label="Ready" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {importResult == null ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Ready to import {validRows.length} users into{' '}
                  <strong>{describeSelection(selection)}</strong> with role <strong>{role}</strong>.{' '}
                  {sendWelcomeEmails
                    ? 'Each non-existing user will receive an invitation set-up link.'
                    : 'Welcome emails are turned off for this import — users will be created without an invitation.'}
                </Typography>
                {importing && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <LinearProgress variant="indeterminate" />
                    <Typography variant="caption" color="text.secondary">
                      {importProgress.stage === 'creating' && 'Creating users…'}
                      {importProgress.stage === 'emailing' &&
                        `Sending ${importProgress.total} invitation${importProgress.total === 1 ? '' : 's'}…`}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon
                  color={importResult.failed === 0 ? 'success' : 'warning'}
                  sx={{ fontSize: 48 }}
                />
                <Typography variant="h6">
                  {importResult.created} user
                  {importResult.created === 1 ? '' : 's'} imported
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importResult.welcomeEmailsSkipped
                    ? 'Welcome emails were not sent (turned off for this import).'
                    : `Invitations sent: ${importResult.emailsSent}${
                        importResult.emailsFailed > 0
                          ? ` · ${importResult.emailsFailed} email${importResult.emailsFailed === 1 ? '' : 's'} failed`
                          : ''
                      }.`}
                </Typography>
                {importResult.skipped_existing > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {importResult.skipped_existing} row
                    {importResult.skipped_existing === 1 ? '' : 's'} skipped — email already exists.
                  </Typography>
                )}
                {importResult.failed > 0 && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="error">
                      {importResult.failed} row{importResult.failed === 1 ? '' : 's'} failed:
                    </Typography>
                    {importResult.failureReasons.map((reason) => (
                      <Typography key={reason} variant="caption" color="error" display="block">
                        {reason}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
        <Button disabled={step === 0 || importing} onClick={() => setStep((s) => s - 1)}>
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          {(() => {
            if (step < 3) {
              return (
                <Button
                  variant="contained"
                  disabled={!canAdvance}
                  onClick={() => setStep((s) => s + 1)}
                >
                  Next
                </Button>
              );
            }
            if (importResult == null) {
              return (
                <Button variant="contained" disabled={importing} onClick={handleImport}>
                  {importing ? 'Importing…' : 'Import'}
                </Button>
              );
            }
            return (
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};

export default ImportUsersFromCsvDrawer;
